import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Box,
  Chip,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import emailTemplatesApi, { EmailTemplate } from '../../services/email-templates-api';
import { contactsAPI } from '../../services/api';
import api from '../../services/api';

interface Contact {
  id: number;
  name?: string;
  email?: string;
  phone_number?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  preselectedContact?: Contact | null;
  onSuccess: () => void;
}

const EmailOutreachDialog: React.FC<Props> = ({
  open,
  onClose,
  preselectedContact,
  onSuccess,
}) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [channels, setChannels] = useState<any[]>([]);

  const [selectedContact, setSelectedContact] = useState<Contact | null>(preselectedContact || null);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<number | ''>('');
  const [customVariables, setCustomVariables] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadData();
      if (preselectedContact) {
        setSelectedContact(preselectedContact);
      }
    }
  }, [open, preselectedContact]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [contactsRes, templatesRes, channelsRes] = await Promise.all([
        contactsAPI.getContacts({ per_page: 100 }),
        emailTemplatesApi.list(true),
        api.get('/inboxes/').then(r => r.data),
      ]);

      setContacts(contactsRes.contacts || []);
      setTemplates(templatesRes);
      setChannels((channelsRes.inboxes || []).filter((c: any) => c.channel_type === 'email'));

      // Auto-select first email channel
      const emailChannels = (channelsRes.inboxes || []).filter((c: any) => c.channel_type === 'email');
      if (emailChannels.length > 0) {
        setSelectedChannel(emailChannels[0].id);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId: number | null) => {
    const template = templates.find(t => t.id === templateId) || null;
    setSelectedTemplate(template);

    // Initialize custom variables
    if (template) {
      const customVars = (template.variables || []).filter(
        v => !v.startsWith('contact.') && !v.startsWith('account.') &&
             !v.startsWith('agent.') && !v.startsWith('conversation.') &&
             !v.startsWith('current_')
      );
      const initialVars: Record<string, string> = {};
      customVars.forEach(v => { initialVars[v] = ''; });
      setCustomVariables(initialVars);
    }
  };

  const handleSend = async () => {
    if (!selectedContact || !selectedTemplate || !selectedChannel) {
      setError('Please select contact, template, and email channel');
      return;
    }

    if (!selectedContact.email) {
      setError('Selected contact has no email address');
      return;
    }

    setSending(true);
    setError(null);

    try {
      // Use public API to create conversation with template
      const response = await api.post('/conversations/public/create-with-template', {
        channel_id: selectedChannel,
        contact_id: selectedContact.id,
        email_template_id: selectedTemplate.id,
        template_variables: customVariables,
      });

      if (response.data.success) {
        onSuccess();
        onClose();
      } else {
        setError(response.data.template_send_error || 'Failed to send email');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  // Get custom variable names
  const customVariableNames = selectedTemplate
    ? (selectedTemplate.variables || []).filter(
        v => !v.startsWith('contact.') && !v.startsWith('account.') &&
             !v.startsWith('agent.') && !v.startsWith('conversation.') &&
             !v.startsWith('current_')
      )
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '85vh' } }}>
      <DialogTitle>Send Email Outreach</DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Autocomplete
                options={contacts.filter(c => c.email)}
                getOptionLabel={(c) => `${c.name || 'Unknown'} (${c.email})`}
                value={selectedContact}
                onChange={(_, value) => setSelectedContact(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Contact" required />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography>{option.name || 'Unknown'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    </Box>
                  </li>
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Email Channel</InputLabel>
                <Select
                  value={selectedChannel}
                  label="Email Channel"
                  onChange={(e) => setSelectedChannel(e.target.value as number)}
                >
                  {channels.map(ch => (
                    <MenuItem key={ch.id} value={ch.id}>{ch.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth required>
                <InputLabel>Email Template</InputLabel>
                <Select
                  value={selectedTemplate?.id || ''}
                  label="Email Template"
                  onChange={(e) => handleTemplateChange(e.target.value as number)}
                >
                  {templates.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                      {t.category && <Chip label={t.category} size="small" sx={{ ml: 1 }} />}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {selectedTemplate && customVariableNames.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Fill in custom variables (contact info is auto-filled):
                </Typography>
                <Grid container spacing={2}>
                  {customVariableNames.map(v => (
                    <Grid item xs={6} key={v}>
                      <TextField
                        fullWidth
                        size="small"
                        label={v}
                        value={customVariables[v] || ''}
                        onChange={(e) => setCustomVariables(prev => ({
                          ...prev,
                          [v]: e.target.value
                        }))}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {selectedTemplate && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Subject: {selectedTemplate.subject}
                </Typography>
                {selectedTemplate.attachments && selectedTemplate.attachments.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Includes {selectedTemplate.attachments.length} attachment(s)
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={16} /> : <SendIcon />}
          onClick={handleSend}
          disabled={sending || !selectedContact || !selectedTemplate || !selectedChannel}
        >
          {sending ? 'Sending...' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailOutreachDialog;
